import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, fromEvent, takeUntil } from 'rxjs';

import { RadioService } from './radio.service';

@Component({
  selector: 'app-radio',
  template: `
    <span class="cursor-pointer">
      <input
        type="radio"
        [checked]="isChecked"
        [disabled]="disabled"
        [attr.name]="name"
        [value]="value"
        class="sr-only" />
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
        <path
          d="M0.5 10C0.5 4.7533 4.7533 0.5 10 0.5C15.2467 0.5 19.5 4.7533 19.5 10C19.5 15.2467 15.2467 19.5 10 19.5C4.7533 19.5 0.5 15.2467 0.5 10Z"
          [ngClass]="{
            'fill-neutral-50': !disabled,
            'stroke-neutral-300': !disabled && !isChecked,
            'stroke-primary-600': !disabled && isChecked,
            'fill-neutral-300': disabled,
            'stroke-neutral-500': disabled,
            'duration-200': true
          }" />
        <rect
          x="5"
          y="5"
          width="10"
          height="10"
          rx="5"
          [ngClass]="{
            'fill-neutral-50': !disabled && !isChecked,
            'fill-primary-600': !disabled && isChecked,
            'fill-neutral-300': disabled && !isChecked,
            'fill-neutral-500': disabled && isChecked,
            'duration-200': true
          }" />
      </svg>
      <span class="prose prose-text-sm ml-3"><ng-content></ng-content></span>
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioComponent),
      multi: true
    }
  ]
})
export class RadioComponent implements OnInit, ControlValueAccessor, OnDestroy {
  isChecked = false;
  name: string | null = null;

  onChange: Function = () => {};
  onTouched: Function = () => {};

  @Input() value: any = null;
  @Input() disabled: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private ngZone: NgZone,
    private elementRef: ElementRef<any>,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(RadioService) private radioService: RadioService | null
  ) {}

  ngOnInit(): void {
    if (this.radioService) {
      this.radioService.name$.pipe(takeUntil(this.destroy$)).subscribe(name => {
        this.name = name;
        this.cdr.markForCheck();
      });
      this.radioService.disabled$.pipe(takeUntil(this.destroy$)).subscribe(disabled => {
        this.disabled = this.disabled || disabled;
        this.cdr.markForCheck();
      });
      this.radioService.selected$.pipe(takeUntil(this.destroy$)).subscribe(value => {
        const isChecked = this.isChecked;
        this.isChecked = this.value === value;
        if (isChecked !== this.isChecked && this.isChecked === false) {
          this.onChange(false);
        }
        this.cdr.markForCheck();
      });
    }

    this.setupClickListener();
  }

  writeValue(obj: any): void {
    this.isChecked = obj;
    this.cdr.markForCheck();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = this.disabled || isDisabled;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupClickListener(): void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent<MouseEvent>(this.elementRef.nativeElement, 'click')
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => {
          /** prevent label click triggered twice. **/
          event.stopPropagation();
          event.preventDefault();
          if (this.disabled || this.isChecked) {
            return;
          }
          this.ngZone.run(() => {
            this.radioService?.select(this.value);
            this.isChecked = true;
            this.onChange(true);
            this.onTouched();
            this.cdr.markForCheck();
          });
        });
    });
  }
}
